import { useEffect, useRef, useState } from "react";
import { useVideoRecorder } from "../hooks/use-video-recorder";
import Button from "@/components/ui/button";

interface VideoRecorderProps {
	onRecordingComplete: (file: File, previewUrl: string) => void;
	existingVideoFile?: File | null;
	onReset?: () => void;
	fullHeight?: boolean;
}

function formatTime(seconds: number): string {
	const mins = Math.floor(seconds / 60);
	const secs = seconds % 60;
	return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

export default function VideoRecorder({ onRecordingComplete, existingVideoFile, onReset, fullHeight = false }: VideoRecorderProps) {
	const {
		isRecording,
		isPaused,
		isProcessing,
		recordedBlob,
		recordedUrl,
		error,
		hasPermission,
		isInitializing,
		recordingTime,
		streamRef,
		initializeCamera,
		startRecording,
		stopRecording,
		pauseRecording,
		resumeRecording,
		resetRecording,
	} = useVideoRecorder();

	const videoPlaybackRef = useRef<HTMLVideoElement>(null);
	const liveVideoRef = useRef<HTMLVideoElement>(null);
	const hasNotifiedRef = useRef(false);
	const onRecordingCompleteRef = useRef(onRecordingComplete);

	// Create a stable URL from the existing video file
	const [existingVideoUrl, setExistingVideoUrl] = useState<string | null>(null);

	// Keep the callback ref updated
	onRecordingCompleteRef.current = onRecordingComplete;

	// Create blob URL from existing video file
	useEffect(() => {
		if (existingVideoFile) {
			const url = URL.createObjectURL(existingVideoFile);
			setExistingVideoUrl(url);
			return () => {
				URL.revokeObjectURL(url);
			};
		} else {
			setExistingVideoUrl(null);
		}
	}, [existingVideoFile]);

	// Show existing video or newly recorded video
	const displayUrl = existingVideoUrl || recordedUrl;

	// Initialize camera on mount (only if no existing video to display)
	useEffect(() => {
		if (hasPermission === null && !existingVideoFile) {
			initializeCamera();
		}
	}, [hasPermission, initializeCamera, existingVideoFile]);

	// Attach stream to video element
	useEffect(() => {
		if (liveVideoRef.current && streamRef.current) {
			liveVideoRef.current.srcObject = streamRef.current;
		}
	}, [streamRef.current]);

	// When recording completes, notify parent and auto-download (only once)
	useEffect(() => {
		if (recordedBlob && recordedUrl && !hasNotifiedRef.current) {
			hasNotifiedRef.current = true;
			const filename = `testimonial-${Date.now()}.webm`;
			const file = new File([recordedBlob], filename, {
				type: recordedBlob.type,
			});
			onRecordingCompleteRef.current(file, recordedUrl);

			// Auto-download the recorded video
			const downloadLink = document.createElement("a");
			downloadLink.href = recordedUrl;
			downloadLink.download = filename;
			document.body.appendChild(downloadLink);
			downloadLink.click();
			document.body.removeChild(downloadLink);
		}
	}, [recordedBlob, recordedUrl]);

	const handleReRecord = () => {
		hasNotifiedRef.current = false;
		resetRecording();
		onReset?.();
		initializeCamera();
	};

	// Cleanup camera stream on unmount (but don't revoke URL if we have an existing video)
	useEffect(() => {
		return () => {
			// Only cleanup camera stream, not the recorded URL if it's being used
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
			}
		};
	}, []);

	const containerClass = fullHeight ? "flex flex-col h-full" : "space-y-4";
	const videoContainerClass = fullHeight ? "flex-1 min-h-0 relative rounded-xl overflow-hidden bg-gray-900" : "relative rounded-xl overflow-hidden bg-gray-900 aspect-video";

	// Show playback if we have a recorded or existing video
	if (displayUrl) {
		return (
			<div className={containerClass}>
				<div className={videoContainerClass}>
					<video ref={videoPlaybackRef} src={displayUrl} controls className="w-full h-full object-cover" />
				</div>
				<div className={`flex justify-center ${fullHeight ? "pt-4 shrink-0" : ""}`}>
					<Button type="button" variant="soft" onClick={handleReRecord}>
						<span className="flex items-center gap-2">
							<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
								/>
							</svg>
							Re-record
						</span>
					</Button>
				</div>
			</div>
		);
	}

	// Show processing state (after recording stops, before blob is ready)
	if (isProcessing) {
		return (
			<div className={fullHeight ? "h-full flex items-center justify-center" : ""}>
				<div className="rounded-xl bg-gray-100 aspect-video flex items-center justify-center">
					<div className="text-center">
						<div className="w-8 h-8 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-3"></div>
						<p className="text-secondary text-sm">Processing video...</p>
					</div>
				</div>
			</div>
		);
	}

	// Show error state
	if (error) {
		return (
			<div className={fullHeight ? "h-full flex items-center justify-center" : ""}>
				<div className="rounded-xl bg-red-50 border border-red-200 p-6 text-center aspect-video flex flex-col items-center justify-center">
					<svg className="w-12 h-12 text-red-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
						/>
					</svg>
					<p className="text-red-700 mb-4">{error}</p>
					<Button type="button" variant="soft" onClick={() => initializeCamera()}>
						Try Again
					</Button>
				</div>
			</div>
		);
	}

	// Show initializing state
	if (isInitializing) {
		return (
			<div className={fullHeight ? "h-full flex items-center justify-center" : ""}>
				<div className="rounded-xl bg-gray-100 aspect-video flex items-center justify-center">
					<div className="text-center">
						<div className="w-8 h-8 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-3"></div>
						<p className="text-secondary text-sm">Initializing camera...</p>
					</div>
				</div>
			</div>
		);
	}

	// Show permission denied state
	if (hasPermission === false) {
		return (
			<div className={fullHeight ? "h-full flex items-center justify-center" : ""}>
				<div className="rounded-xl bg-gray-100 aspect-video flex items-center justify-center p-6">
					<div className="text-center">
						<svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
							/>
						</svg>
						<p className="text-secondary mb-4">Camera access is required to record a video testimonial.</p>
						<Button type="button" variant="soft" onClick={() => initializeCamera()}>
							Enable Camera
						</Button>
					</div>
				</div>
			</div>
		);
	}

	// Show live camera view with recording controls
	return (
		<div className={containerClass}>
			{/* Live camera preview */}
			<div className={videoContainerClass}>
				<video ref={liveVideoRef} autoPlay muted playsInline className="w-full h-full object-cover scale-x-[-1]" />

				{/* Recording indicator */}
				{isRecording && (
					<div className="absolute top-4 left-4 flex items-center gap-2 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1.5">
						<span className={`w-3 h-3 rounded-full ${isPaused ? "bg-yellow-500" : "bg-red-500 animate-pulse"}`}></span>
						<span className="text-white text-sm font-medium">{formatTime(recordingTime)}</span>
					</div>
				)}

				{/* Tips overlay at bottom of video */}
				{fullHeight && !isRecording && (
					<div className="absolute bottom-4 left-4 right-4 bg-black/60 backdrop-blur-sm rounded-lg p-3">
						<p className="text-sm font-medium text-white mb-1">Tips for a great video:</p>
						<ul className="text-xs text-white/80 space-y-0.5">
							<li>• Position yourself in the center of the frame</li>
							<li>• Make sure your face is well-lit</li>
							<li>• Speak clearly and at a comfortable pace</li>
						</ul>
					</div>
				)}
			</div>

			{/* Tips - only show when not fullHeight */}
			{!fullHeight && (
				<div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
					<p className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
						<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
							/>
						</svg>
						Tips for a great video:
					</p>
					<ul className="text-sm text-amber-700 space-y-1">
						<li className="flex items-start gap-2">
							<span>•</span>
							<span>Position yourself in the center of the frame</span>
						</li>
						<li className="flex items-start gap-2">
							<span>•</span>
							<span>Make sure your face is well-lit</span>
						</li>
						<li className="flex items-start gap-2">
							<span>•</span>
							<span>Speak clearly and at a comfortable pace</span>
						</li>
					</ul>
				</div>
			)}

			{/* Controls */}
			<div className={`flex justify-center gap-3 ${fullHeight ? "pt-4 shrink-0" : ""}`}>
				{!isRecording ? (
					<Button type="button" variant="light" onClick={startRecording} className="flex items-center gap-2">
						<svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 24 24">
							<circle cx="12" cy="12" r="10" />
						</svg>
						Start Recording
					</Button>
				) : (
					<>
						{isPaused ? (
							<Button type="button" variant="soft" onClick={resumeRecording} className="flex items-center gap-2">
								<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
									<path d="M8 5v14l11-7z" />
								</svg>
								Resume
							</Button>
						) : (
							<Button type="button" variant="soft" onClick={pauseRecording} className="flex items-center gap-2">
								<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
									<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
								</svg>
								Pause
							</Button>
						)}
						<Button type="button" variant="light" onClick={stopRecording} className="flex items-center gap-2">
							<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
								<rect x="6" y="6" width="12" height="12" rx="2" />
							</svg>
							Stop Recording
						</Button>
					</>
				)}
			</div>
		</div>
	);
}
